function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { motion, AnimatePresence } from 'framer-motion';
import { formatBytes } from '../utils/helpers';
import Alert from './Alert';
import { StyledWrapper, StyledDraggingIndicator, StyledLoader, StyledIcon } from './DropZone.styled';
var DropZone = function DropZone(_ref) {
  var loading = _ref.loading,
    compact = _ref.compact,
    disabled = _ref.disabled,
    onDrop = _ref.onDrop,
    edges = _ref.edges,
    dropzoneLabel = _ref.dropzoneLabel,
    dropzoneDescription = _ref.dropzoneDescription,
    dropzoneProps = _ref.dropzoneProps,
    listAcceptedFiles = _ref.listAcceptedFiles;
  var _useDropzone = useDropzone(_extends({
      onDrop: onDrop,
      disabled: disabled
    }, dropzoneProps)),
    getRootProps = _useDropzone.getRootProps,
    getInputProps = _useDropzone.getInputProps,
    isDragActive = _useDropzone.isDragActive,
    isDragAccept = _useDropzone.isDragAccept,
    isDragReject = _useDropzone.isDragReject,
    acceptedFiles = _useDropzone.acceptedFiles,
    fileRejections = _useDropzone.fileRejections;
  var hasErrors = fileRejections.length > 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledWrapper, _extends({
    compact: compact ? 'yes' : 'no',
    disabled: disabled,
    edges: edges,
    errors: hasErrors ? 'yes' : 'no'
  }, getRootProps(), {
    sx: {
      mb: 1
    }
  }), isDragActive && /*#__PURE__*/React.createElement(StyledDraggingIndicator, {
    isDragReject: isDragReject
  }), loading && !hasErrors && /*#__PURE__*/React.createElement(StyledLoader, null), /*#__PURE__*/React.createElement("input", getInputProps()), isDragAccept && /*#__PURE__*/React.createElement(StyledIcon, {
    compact: compact
  }, /*#__PURE__*/React.createElement(CheckCircleIcon, {
    color: "inherit",
    fontSize: "inherit"
  })), isDragReject && /*#__PURE__*/React.createElement(StyledIcon, {
    compact: compact
  }, /*#__PURE__*/React.createElement(WarningIcon, {
    color: "inherit",
    fontSize: "inherit"
  })), !isDragAccept && !isDragReject && /*#__PURE__*/React.createElement(StyledIcon, {
    compact: compact
  }, /*#__PURE__*/React.createElement(CloudUploadIcon, {
    color: "inherit",
    fontSize: "inherit"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    align: "center"
  }, isDragReject ? 'Error' : dropzoneLabel), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "textSecondary"
  }, isDragReject ? 'The selected file(s) will not be accepted' : dropzoneDescription)), /*#__PURE__*/React.createElement(AnimatePresence, null, listAcceptedFiles && acceptedFiles.length > 0 && /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      translateY: -20,
      opacity: 0
    },
    animate: {
      translateY: 0,
      opacity: 1
    },
    exit: {
      translateY: -20,
      opacity: 0
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut'
    }
  }, acceptedFiles.map(function (file) {
    return /*#__PURE__*/React.createElement(Alert, {
      variant: "standard",
      severity: "success",
      key: file.path,
      style: {
        marginBottom: 5
      }
    }, file.path);
  })), hasErrors && /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      translateY: -20,
      opacity: 0
    },
    animate: {
      translateY: 0,
      opacity: 1
    },
    exit: {
      translateY: -20,
      opacity: 0
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut'
    }
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "standard"
  }, fileRejections.map(function (_ref2) {
    var file = _ref2.file,
      errors = _ref2.errors;
    return /*#__PURE__*/React.createElement("div", {
      key: file.path
    }, file.path, /*#__PURE__*/React.createElement("ul", null, errors.map(function (e) {
      if (e.code === 'file-too-large') {
        return /*#__PURE__*/React.createElement("li", {
          key: e.code
        }, "File size is larger than", ' ', formatBytes(dropzoneProps.maxSize));
      }
      return /*#__PURE__*/React.createElement("li", {
        key: e.code
      }, e.message);
    })));
  })))));
};
DropZone.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Whether or not to show a list of accepted files.
   */
  listAcceptedFiles: PropTypes.bool,
  /**
   * Show progress spinner
   */
  loading: PropTypes.bool,
  /**
   * Compact Layout
   */
  compact: PropTypes.bool,
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Text label displayed in the dropzone
   */
  dropzoneLabel: PropTypes.string,
  /**
   * Text description displayed in the dropzone
   */
  dropzoneDescription: PropTypes.string,
  /**
   * Pass props for react-dropzone. See https://react-dropzone.js.org/
   */
  dropzoneProps: PropTypes.object,
  /**
   * Function: do something when user drops image
   */
  onDrop: PropTypes.func.isRequired,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp'])
} : {};
DropZone.defaultProps = {
  listAcceptedFiles: false,
  disabled: false,
  dropzoneLabel: 'Upload file',
  dropzoneDescription: 'Drag and drop a file here, or click to select a file',
  edges: 'normal',
  loading: false,
  dropzoneProps: null,
  compact: false
};
export default DropZone;